import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase-auth';
import { useAuth } from '../../../contexts/AuthContextLazy';
// import { showError } from '../../../utils/toast';
import toast from 'react-hot-toast';
const showError = (msg: string) => toast.error(msg);
import { colors } from '../../../lib/colors';
import { 
  ArrowLeftIcon,
  UserIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon,
  BellIcon,
  ShieldCheckIcon,
  ChevronRightIcon,
  FolderIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import ClientComplianceDocuments from '../../../components/compliance/ClientComplianceDocuments';
import EditPartyModal from '../../../components/portal/EditPartyModal';

interface ClientUser {
  id: string;
  full_name: string;
  email: string;
  status: string;
  two_factor_enabled: boolean;
  created_at: string;
  last_login_at: string | null;
}

interface Tab {
  id: string;
  name: string;
  icon: any;
  count?: number;
}

export const ClientDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  const [party, setParty] = useState<any>(null);
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  const [advisor, setAdvisor] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [documentCount, setDocumentCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [questionnaireCount, setQuestionnaireCount] = useState(0);
  const [relationships, setRelationships] = useState<any[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (id) {
      fetchClientData();
    }
  }, [id]);

  const fetchClientData = async () => {
    try {
      // Fetch party data
      const { data: partyData, error: partyError } = await supabase
        .from('parties')
        .select(`
          *,
          advisor:platform_users!assigned_advisor_id (
            id,
            full_name,
            email,
            auth_user_id
          )
        `)
        .eq('id', id)
        .single();

      if (partyError) throw partyError;
      setParty(partyData);
      setAdvisor(partyData.advisor);

      // Fetch client users
      const { data: usersData, error: usersError } = await supabase
        .from('client_users')
        .select('*')
        .eq('party_id', id)
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;
      setClientUsers(usersData || []);

      // Fetch document count
      const { count: docCount } = await supabase
        .from('compliance_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', id);
      
      setDocumentCount(docCount || 0);

      // Fetch message thread count
      const { count: msgCount } = await supabase
        .from('message_threads')
        .select('*', { count: 'exact', head: true })
        .eq('party_id', id);
      
      setMessageCount(msgCount || 0);

      // Fetch project count
      const { count: projCount } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', id);
      
      setProjectCount(projCount || 0);

      // Fetch questionnaire count
      const { count: questionnaireCount } = await supabase
        .from('questionnaire_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('party_id', id)
        .neq('status', 'draft');

      setQuestionnaireCount(questionnaireCount || 0);

      // Fetch relationships (both directions)
      const { data: relData } = await supabase
        .from('party_relationships')
        .select(`
          id,
          relationship_type,
          metadata,
          from_party:from_party_id (id, name, type, primary_email),
          to_party:to_party_id (id, name, type, primary_email)
        `)
        .or(`from_party_id.eq.${id},to_party_id.eq.${id}`);

      if (relData) {
        // Transform to show the "other" party in each relationship
        const transformedRels = relData.map((rel: any) => ({
          id: rel.id,
          relationship_type: rel.relationship_type,
          metadata: rel.metadata,
          related_party: rel.from_party?.id === id ? rel.to_party : rel.from_party,
          direction: rel.from_party?.id === id ? 'outgoing' : 'incoming'
        }));
        setRelationships(transformedRels);
      }
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error fetching client data:', error);
        }

      }
      showError('Failed to load client data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartConversation = () => {
    setShowNewMessageModal(true);
  };

  const createMessageThread = async (subject: string, threadType: string, priority: string, initialMessage: string) => {
    try {
      const { data, error } = await supabase
        .rpc('create_message_thread', {
          p_party_id: id,
          p_subject: subject,
          p_thread_type: threadType,
          p_priority: priority,
          p_initial_message: initialMessage || null
        });

      if (error) throw error;
      
      // Navigate to the messages page with the new thread
      navigate(`/portal/messages/${data}`);
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error creating thread:', error);
        }

      }
      showError('Failed to create message thread');
    }
  };

  if (!hasRole(['super_admin', 'pcm_staff'])) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">You don't have permission to view this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Skeleton Header */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 pb-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="animate-pulse">
              <div className="h-4 w-32 bg-gray-700 rounded mb-6"></div>
              <div className="flex items-start space-x-4">
                <div className="w-20 h-20 bg-gray-700 rounded-2xl"></div>
                <div>
                  <div className="h-8 w-64 bg-gray-700 rounded mb-2"></div>
                  <div className="h-4 w-48 bg-gray-700 rounded"></div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-white/10 rounded-xl p-4">
                    <div className="h-4 w-20 bg-gray-700 rounded mb-2"></div>
                    <div className="h-6 w-12 bg-gray-700 rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* Skeleton Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white portal-content-shadow rounded-xl shadow-sm p-6 animate-pulse">
            <div className="h-6 w-48 bg-gray-200 rounded mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!party) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Client not found</p>
      </div>
    );
  }

  // Define tabs
  const tabs: Tab[] = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'projects', name: 'Projects', icon: FolderIcon, count: projectCount },
    { id: 'questionnaires', name: 'Questionnaires', icon: ClipboardDocumentListIcon, count: questionnaireCount },
    { id: 'documents', name: 'Documents', icon: DocumentTextIcon, count: documentCount },
    { id: 'communications', name: 'Communications', icon: ChatBubbleLeftRightIcon, count: messageCount },
    { id: 'activity', name: 'Activity', icon: ClockIcon }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Enhanced Header with Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent"></div>
        <div className="relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Back Button */}
            <Link
              to="/portal/clients"
              className="inline-flex items-center text-sm text-gray-300 hover:text-white transition-colors mb-6"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Clients
            </Link>

            {/* Client Header Info */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div className="flex items-start space-x-4">
                <div className="relative">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-xl">
                    {party.type === 'individual' ? (
                      <UserIcon className="h-10 w-10 text-white" />
                    ) : (
                      <BuildingOfficeIcon className="h-10 w-10 text-white" />
                    )}
                  </div>
                  <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-gray-900 ${
                    party.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                  }`}></div>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">
                    {party.full_name || party.name}
                  </h1>
                  <p className="text-gray-300 mt-1">
                    {party.type === 'individual' ? 'Individual Client' : 'Organization'}
                    {party.tax_id && ` • Tax ID: ${party.tax_id}`}
                  </p>
                  {advisor && (
                    <p className="text-sm text-gray-400 mt-2">
                      Advisor: {advisor.full_name}
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleStartConversation}
                  className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-medium rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-xl"
                >
                  <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                  Send Message
                </button>
                <button
                  onClick={() => setShowEditModal(true)}
                  className="inline-flex items-center px-5 py-2.5 bg-white/10 backdrop-blur-sm text-white font-medium rounded-lg hover:bg-white/20 transition-all border border-white/20"
                >
                  <PencilIcon className="h-5 w-5 mr-2" />
                  Edit Profile
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-300 text-sm">Projects</p>
                    <p className="text-2xl font-bold text-white">{projectCount}</p>
                  </div>
                  <FolderIcon className="h-8 w-8 text-indigo-400" />
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-300 text-sm">Questionnaires</p>
                    <p className="text-2xl font-bold text-white">{questionnaireCount}</p>
                  </div>
                  <ClipboardDocumentListIcon className="h-8 w-8 text-purple-400" />
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-300 text-sm">Documents</p>
                    <p className="text-2xl font-bold text-white">{documentCount}</p>
                  </div>
                  <DocumentTextIcon className="h-8 w-8 text-amber-400" />
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-300 text-sm">Messages</p>
                    <p className="text-2xl font-bold text-white">{messageCount}</p>
                  </div>
                  <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-400" />
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-300 text-sm">Portal Users</p>
                    <p className="text-2xl font-bold text-white">{clientUsers.length}</p>
                  </div>
                  <UserGroupIcon className="h-8 w-8 text-green-400" />
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-300 text-sm">Status</p>
                    <p className="text-2xl font-bold text-white capitalize">{party.status}</p>
                  </div>
                  <ShieldCheckIcon className="h-8 w-8 text-orange-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white portal-content-shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors
                    ${activeTab === tab.id
                      ? 'border-amber-500 text-amber-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                  {tab.count !== undefined && (
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                      activeTab === tab.id 
                        ? 'bg-amber-100 text-amber-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Contact Information Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-gray-600" />
                  Contact Information
                </h3>
              </div>
              <div className="p-6 space-y-4">
                {party.primary_email && (
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <EnvelopeIcon className="h-5 w-5 text-amber-600" />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{party.primary_email}</p>
                    </div>
                  </div>
                )}
                
                {party.primary_phone && (
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <PhoneIcon className="h-5 w-5 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{party.primary_phone}</p>
                    </div>
                  </div>
                )}
                
                {(party.address_line1 || party.city || party.state) && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <MapPinIcon className="h-5 w-5 text-green-600" />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Address</p>
                      <div className="font-medium text-gray-900">
                        {party.address_line1 && <p>{party.address_line1}</p>}
                        {party.address_line2 && <p>{party.address_line2}</p>}
                        {(party.city || party.state || party.postal_code) && (
                          <p>
                            {party.city}{party.city && party.state && ', '}
                            {party.state} {party.postal_code}
                          </p>
                        )}
                        {party.country && <p>{party.country}</p>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Portal Users Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <UserGroupIcon className="h-5 w-5 mr-2 text-gray-600" />
                    Portal Users
                  </h3>
                  <Link
                    to={`/portal/clients/${id}/users/new`}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add User
                  </Link>
                </div>
              </div>
              <div className="p-6">
                {clientUsers.length === 0 ? (
                  <div className="text-center py-6">
                    <UserGroupIcon className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="mt-2 text-sm text-gray-500">No portal users yet</p>
                    <p className="text-xs text-gray-400 mt-1">Add users to grant portal access</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {clientUsers.map((clientUser) => (
                      <div key={clientUser.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-gray-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{clientUser.full_name}</p>
                            <p className="text-sm text-gray-500">{clientUser.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {clientUser.two_factor_enabled && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              <ShieldCheckIcon className="h-3 w-3 mr-1" />
                              2FA
                            </span>
                          )}
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            clientUser.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {clientUser.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Relationships Card */}
            {relationships.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <UserGroupIcon className="h-5 w-5 mr-2 text-gray-600" />
                    Relationships
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {relationships.map((rel) => (
                      <Link
                        key={rel.id}
                        to={`/portal/clients/${rel.related_party?.id}`}
                        className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            {rel.related_party?.type === 'individual' ? (
                              <UserIcon className="h-5 w-5 text-blue-600" />
                            ) : (
                              <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{rel.related_party?.name}</p>
                            <p className="text-sm text-gray-500 capitalize">
                              {rel.relationship_type?.replace(/_/g, ' ')}
                              {rel.metadata?.ownership_percentage && ` (${rel.metadata.ownership_percentage}%)`}
                            </p>
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link
                to={`/portal/documents?client=${id}`}
                className="group relative rounded-xl border border-gray-200 bg-white portal-content-shadow p-6 hover:shadow-md transition-all hover:border-amber-300"
              >
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                      <DocumentTextIcon className="h-6 w-6 text-amber-600" />
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">View Documents</p>
                    <p className="text-sm text-gray-500">Access all client documents</p>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-auto group-hover:text-amber-600 transition-colors" />
                </div>
              </Link>
              
              <Link
                to={`/portal/messages?client=${id}`}
                className="group relative rounded-xl border border-gray-200 bg-white portal-content-shadow p-6 hover:shadow-md transition-all hover:border-blue-300"
              >
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">View Messages</p>
                    <p className="text-sm text-gray-500">Communication history</p>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-auto group-hover:text-blue-600 transition-colors" />
                </div>
              </Link>
            </div>
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="space-y-6">
            <ProjectsSection clientId={id!} clientName={party.name} />
          </div>
        )}

        {activeTab === 'questionnaires' && (
          <div className="space-y-6">
            <QuestionnairesSection clientId={id!} clientName={party.full_name || party.name} clientEmail={party.primary_email} clientPhone={party.primary_phone} />
          </div>
        )}

        {activeTab === 'documents' && (
          <div>
            <ClientComplianceDocuments clientId={id!} clientName={party.name} />
          </div>
        )}

        {activeTab === 'communications' && (
          <div className="bg-white portal-content-shadow rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="text-center py-12">
              <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Communication History</h3>
              <p className="mt-1 text-sm text-gray-500">
                View all messages and communication threads with this client
              </p>
              <div className="mt-6">
                <Link
                  to={`/portal/messages?client=${id}`}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700"
                >
                  View All Messages
                </Link>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="bg-white portal-content-shadow rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="text-center py-12">
              <ClockIcon className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Activity Timeline</h3>
              <p className="mt-1 text-sm text-gray-500">
                Recent activities and interactions will appear here
              </p>
            </div>
          </div>
        )}
      </div>

      {/* New Message Modal */}
      {showNewMessageModal && (
        <NewMessageModal
          clientName={party.full_name || party.name}
          onClose={() => setShowNewMessageModal(false)}
          onCreate={createMessageThread}
        />
      )}

      {/* Edit Party Modal */}
      <EditPartyModal
        isOpen={showEditModal}
        party={party}
        onClose={() => setShowEditModal(false)}
        onSuccess={() => {
          fetchClientData();
        }}
      />
    </div>
  );
};

// New Message Modal Component
const NewMessageModal: React.FC<{
  clientName: string;
  onClose: () => void;
  onCreate: (subject: string, threadType: string, priority: string, initialMessage: string) => void;
}> = ({ clientName, onClose, onCreate }) => {
  const [subject, setSubject] = useState('');
  const [threadType, setThreadType] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [initialMessage, setInitialMessage] = useState('');
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim()) return;

    setCreating(true);
    await onCreate(subject, threadType, priority, initialMessage);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white portal-content-shadow rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          New Message to {clientName}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
              placeholder="Enter subject..."
              required
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={threadType}
                onChange={(e) => setThreadType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="general">General</option>
                <option value="investment_request">Investment Request</option>
                <option value="document_request">Document Request</option>
                <option value="service_request">Service Request</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Initial Message
            </label>
            <textarea
              value={initialMessage}
              onChange={(e) => setInitialMessage(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
              placeholder="Type your message..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Start Conversation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Questionnaires Section Component
const QuestionnairesSection: React.FC<{ 
  clientId: string; 
  clientName: string; 
  clientEmail?: string; 
  clientPhone?: string; 
}> = ({ clientId, clientName, clientEmail, clientPhone }) => {
  const [questionnaires, setQuestionnaires] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSendDrawer, setShowSendDrawer] = useState(false);

  useEffect(() => {
    fetchQuestionnaires();
    
    // Set up real-time subscription
    const channel = supabase
      .channel(`client_questionnaires:${clientId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'questionnaire_sessions',
          filter: `party_id=eq.${clientId}`
        },
        (payload: any) => {
          console.log('Questionnaire updated:', payload);
          fetchQuestionnaires(); // Refresh the list
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clientId]);

  const fetchQuestionnaires = async () => {
    try {
      const { data, error } = await supabase
        .from('advisor_questionnaire_dashboard')
        .select('*')
        .eq('party_id', clientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuestionnaires(data || []);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching questionnaires:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = (status: string, envelopeStatus: string) => {
    if (envelopeStatus === 'sent') {
      return { label: 'Awaiting Signature', color: 'bg-purple-100 text-purple-800' };
    }
    if (envelopeStatus === 'completed') {
      return { label: 'Completed', color: 'bg-green-100 text-green-800' };
    }
    
    const statusLabels = {
      draft: 'Draft',
      sent: 'Sent',
      in_progress: 'In Progress',
      completed: 'Completed',
      submitted: 'Submitted',
      approved: 'Approved'
    };
    
    const statusColors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-purple-100 text-purple-800',
      submitted: 'bg-green-100 text-green-800',
      approved: 'bg-green-100 text-green-800'
    };
    
    return {
      label: statusLabels[status as keyof typeof statusLabels] || status,
      color: statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'
    };
  };

  const ProgressBar = ({ progress }: { progress: number }) => (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div 
        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>
  );

  const StatusChip = ({ status, envelopeStatus }: { status: string, envelopeStatus: string }) => {
    const { label, color } = getStatusDisplay(status, envelopeStatus);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white portal-content-shadow rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <ClipboardDocumentListIcon className="h-5 w-5 mr-2 text-gray-600" />
          Questionnaires
        </h3>
        <button
          onClick={() => setShowSendDrawer(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Send Questionnaire
        </button>
      </div>
      
      {questionnaires.length === 0 ? (
        <div className="p-6 text-center">
          <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-300" />
          <h4 className="mt-2 text-sm font-medium text-gray-900">No questionnaires sent yet</h4>
          <p className="mt-1 text-sm text-gray-500">
            Send a questionnaire to {clientName} to get started
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowSendDrawer(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Send First Questionnaire
            </button>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {questionnaires.map((questionnaire) => (
                <tr key={questionnaire.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {questionnaire.template_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        v{questionnaire.template_version} • {questionnaire.questionnaire_type}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(questionnaire.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-24">
                      <ProgressBar progress={questionnaire.progress_percentage || 0} />
                      <p className="text-xs text-gray-500 mt-1">{questionnaire.progress_percentage || 0}%</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusChip 
                      status={questionnaire.status} 
                      envelopeStatus={questionnaire.envelope_status || 'none'} 
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button className="text-gray-400 hover:text-gray-600 p-1" title="View details">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      {questionnaire.progress_percentage >= 90 && (
                        <button className="text-gray-400 hover:text-gray-600 p-1" title="Download PDF">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                      )}
                      {questionnaire.status === 'sent' && questionnaire.progress_percentage < 10 && (
                        <button className="text-blue-600 hover:text-blue-800 p-1" title="Send reminder">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Send Questionnaire Drawer would go here */}
      {showSendDrawer && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowSendDrawer(false)} />
          <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Send Questionnaire</h3>
              <button
                onClick={() => setShowSendDrawer(false)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="text-center py-12">
              <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">Send questionnaire drawer coming soon</p>
              <p className="text-xs text-gray-400 mt-1">Full implementation in next task</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Projects Section Component - Enhanced for Engagement Tracking
const ProjectsSection: React.FC<{ clientId: string; clientName: string }> = ({ clientId, clientName }) => {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, [clientId]);

  const fetchProjects = async () => {
    try {
      // First try the new project_parties relationship
      const { data: projectParties, error: ppError } = await supabase
        .from('project_parties')
        .select(`
          project_id,
          role,
          projects (
            *,
            tasks (
              id,
              status
            )
          )
        `)
        .eq('party_id', clientId);

      if (!ppError && projectParties && projectParties.length > 0) {
        // Use projects from project_parties
        const projectList = projectParties
          .filter((pp: any) => pp.projects)
          .map((pp: any) => pp.projects);
        setProjects(projectList);
      } else {
        // Fallback to old client_id relationship for backwards compatibility
        const { data, error } = await supabase
          .from('projects')
          .select(`
            *,
            tasks (
              id,
              status
            )
          `)
          .eq('client_id', clientId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setProjects(data || []);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching projects:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'proposal':
        return 'bg-purple-100 text-purple-800';
      case 'engaged':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'on_hold':
        return 'bg-yellow-100 text-yellow-800';
      case 'complete':
      case 'completed':
        return 'bg-emerald-100 text-emerald-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      proposal: 'Proposal',
      engaged: 'Engaged',
      in_progress: 'In Progress',
      active: 'Active',
      on_hold: 'On Hold',
      complete: 'Complete',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return labels[status] || status;
  };

  const calculateProgress = (project: any) => {
    // Use tasks_completed/tasks_total if available (new schema)
    if (project.tasks_total && project.tasks_total > 0) {
      return Math.round((project.tasks_completed || 0) / project.tasks_total * 100);
    }
    // Fallback to counting tasks array
    const tasks = project.tasks || project.project_tasks || [];
    if (!tasks || tasks.length === 0) return 0;
    const completedTasks = tasks.filter((t: any) => t.status === 'completed').length;
    return Math.round((completedTasks / tasks.length) * 100);
  };

  if (loading) {
    return (
      <div className="bg-white portal-content-shadow rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <FolderIcon className="h-5 w-5 mr-2 text-gray-600" />
          Projects
        </h3>
        <Link
          to={`/portal/projects/new?partyId=${clientId}`}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          New Project
        </Link>
      </div>
      
      {projects.length === 0 ? (
        <div className="p-6 text-center">
          <FolderIcon className="mx-auto h-12 w-12 text-gray-300" />
          <h4 className="mt-2 text-sm font-medium text-gray-900">No projects yet</h4>
          <p className="mt-1 text-sm text-gray-500">
            Create a project to start tracking work for {clientName}
          </p>
          <div className="mt-6">
            <Link
              to={`/portal/projects/new?partyId=${clientId}`}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Create First Project
            </Link>
          </div>
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {projects.map((project) => {
            const progress = calculateProgress(project);
            const hasPhases = project.total_phases && project.total_phases > 1;
            const taskCount = project.tasks_total || project.tasks?.length || project.project_tasks?.length || 0;
            return (
              <Link
                key={project.id}
                to={`/portal/projects/${project.id}`}
                className="block p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      {project.project_number && (
                        <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          {project.project_number}
                        </span>
                      )}
                      <h4 className="text-base font-medium text-gray-900">{project.name}</h4>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status)}`}>
                        {getStatusLabel(project.status)}
                      </span>
                    </div>
                    {project.description && (
                      <p className="mt-1 text-sm text-gray-600 line-clamp-2">{project.description}</p>
                    )}
                    <div className="mt-3 flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                      {hasPhases && (
                        <span className="flex items-center">
                          <span className="font-medium text-gray-700">Phase {project.current_phase}/{project.total_phases}</span>
                        </span>
                      )}
                      {(project.engaged_date || project.start_date) && (
                        <span className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {project.engaged_date
                            ? `Engaged ${new Date(project.engaged_date).toLocaleDateString()}`
                            : `Started ${new Date(project.start_date).toLocaleDateString()}`
                          }
                        </span>
                      )}
                      {taskCount > 0 && (
                        <span className="flex items-center">
                          <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1" />
                          {project.tasks_completed || 0}/{taskCount} tasks
                        </span>
                      )}
                      {project.fee_total && (
                        <span className="flex items-center text-green-600 font-medium">
                          ${project.fee_total.toLocaleString()}
                        </span>
                      )}
                    </div>
                    {taskCount > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">Progress</span>
                          <span className="font-medium text-gray-900">{progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-4 flex-shrink-0" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};